import { useEffect, useCallback, useState } from "react";
import { X, ChevronLeft, ChevronRight, Maximize } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar, Cell } from "recharts";

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function PresentationMode({ data, meta, mes, loja, meses, lojas, evolucao, financeiro, listas, onClose }) {
  const [slide, setSlide] = useState(0);
  const TOTAL = 8;

  const next = useCallback(() => setSlide((s) => Math.min(TOTAL - 1, s + 1)), []);
  const prev = useCallback(() => setSlide((s) => Math.max(0, s - 1)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => {});
  };

  const sel = data.find((d) => d.mes === mes && d.loja === loja) || {};
  const custoTotal = data.reduce((a, d) => a + (d.custo || 0), 0);
  const mediaGeral = data.length ? (data.reduce((a, d) => a + (d.acuracidade || 0), 0) / data.length).toFixed(2) : 0;
  const topCausas = [...data].sort((a, b) => (b.custo || 0) - (a.custo || 0)).slice(0, 5);
  const acoes = listas.filter((l) => l.tipo === "acoes");

  const slides = [
    // 1 Capa
    <div key="s1" className="text-center space-y-6">
      <div className="inline-block px-4 py-1.5 rounded-full border border-[#2DC26B] text-[#2DC26B] text-sm font-semibold tracking-widest uppercase">Relatório Executivo</div>
      <h1 className="text-5xl font-extrabold text-[#0F6B3A] tracking-tight">Resultados de Acuracidade WMS</h1>
      <p className="text-xl text-[#5A6B62]">Inventários de Cosméticos · {mes} · Loja {loja}</p>
      <p className="text-sm text-[#8A9A90]">{new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}</p>
    </div>,
    // 2 KPIs
    <div key="s2" className="w-full max-w-4xl space-y-8">
      <h2 className="text-3xl font-bold text-[#0F6B3A] text-center">KPIs Principais de Inventário</h2>
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-[#EAF7EF] border border-[#D9E3DC] rounded-xl p-6 text-center">
          <p className="text-sm text-[#5A6B62] uppercase tracking-wide">Acuracidade ({loja})</p>
          <p className="text-5xl font-extrabold text-[#0F6B3A] mt-3">{(sel.acuracidade ?? 0).toFixed(2)}%</p>
          <p className="text-sm mt-2 font-semibold" style={{ color: (sel.acuracidade ?? 0) >= meta ? "#047857" : "#B91C1C" }}>Meta {meta}%</p>
        </div>
        <div className="bg-[#EAF7EF] border-2 border-[#B7E4C7] rounded-xl p-6 text-center">
          <p className="text-sm text-[#22A85B] uppercase tracking-wide">Custo Acumulado (Ano)</p>
          <p className="text-4xl font-extrabold text-[#2DC26B] mt-3">{fmtBRL(custoTotal)}</p>
          <p className="text-sm text-[#22A85B] mt-2">Divergência total</p>
        </div>
        <div className="bg-[#EAF7EF] border border-[#D9E3DC] rounded-xl p-6 text-center">
          <p className="text-sm text-[#5A6B62] uppercase tracking-wide">Acuracidade Média Geral</p>
          <p className="text-5xl font-extrabold text-[#0F6B3A] mt-3">{mediaGeral}%</p>
          <p className="text-sm text-[#5A6B62] mt-2">{data.length} registros</p>
        </div>
      </div>
    </div>,
    // 3 Financeiro
    <div key="s3" className="w-full max-w-4xl space-y-6">
      <h2 className="text-3xl font-bold text-[#0F6B3A] text-center">Impacto Financeiro por Mês</h2>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={financeiro} margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="mes" tick={{ fontSize: 12, fill: "#5A6B62" }} />
            <YAxis tick={{ fontSize: 11, fill: "#8A9A90" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ border: "1px solid #D9E3DC", borderRadius: 8 }} />
            <Bar dataKey="custo" name="Custo divergência" radius={[6, 6, 0, 0]}>
              {financeiro.map((f, i) => <Cell key={i} fill={i === financeiro.length - 1 ? "#2DC26B" : "#0F6B3A"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-center text-lg text-[#5A6B62]">Custo acumulado no ano: <strong className="text-[#2DC26B]">{fmtBRL(custoTotal)}</strong></p>
    </div>,
    // 4 Evolução
    <div key="s4" className="w-full max-w-4xl space-y-6">
      <h2 className="text-3xl font-bold text-[#0F6B3A] text-center">Evolução da Acuracidade — Loja {loja}</h2>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={evolucao} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="mes" tick={{ fontSize: 12, fill: "#5A6B62" }} />
            <YAxis domain={[80, 100]} tick={{ fontSize: 11, fill: "#8A9A90" }} unit="%" />
            <Tooltip contentStyle={{ border: "1px solid #D9E3DC", borderRadius: 8 }} />
            <ReferenceLine y={meta} stroke="#2DC26B" strokeDasharray="6 4" strokeWidth={2.5} label={{ value: `Meta ${meta}%`, fill: "#2DC26B", fontSize: 12, fontWeight: 700 }} />
            <Line type="monotone" dataKey="acuracidade" name="Acuracidade %" stroke="#0F6B3A" strokeWidth={3} dot={{ r: 5, fill: "#0F6B3A" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>,
    // 5 Top causas
    <div key="s5" className="w-full max-w-3xl space-y-6">
      <h2 className="text-3xl font-bold text-[#0F6B3A] text-center">Top 5 Divergências de Custo</h2>
      <div className="space-y-4">
        {topCausas.map((t, i) => (
          <div key={i} className="flex items-center gap-4 bg-[#F4F8F5] border border-[#D9E3DC] rounded-xl p-4">
            <span className="w-10 h-10 rounded-full bg-[#0F6B3A] text-white font-extrabold flex items-center justify-center text-lg">{i + 1}</span>
            <div className="flex-1">
              <p className="font-bold text-[#0F2A1F] text-lg">{t.loja} · {t.mes}</p>
              <p className="text-sm text-[#5A6B62]">Acuracidade {(t.acuracidade ?? 0).toFixed(2)}%</p>
            </div>
            <p className="text-xl font-extrabold text-[#2DC26B]">{fmtBRL(t.custo)}</p>
          </div>
        ))}
      </div>
    </div>,
    // 6 Heatmap
    <div key="s6" className="w-full max-w-4xl space-y-6">
      <h2 className="text-3xl font-bold text-[#0F6B3A] text-center">Mapa de Calor — Acuracidade por Loja × Mês</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="p-2 text-left text-[#5A6B62] font-semibold">Loja</th>
              {meses.map((m) => <th key={m} className="p-2 text-[#5A6B62] font-semibold">{m}</th>)}
            </tr>
          </thead>
          <tbody>
            {lojas.map((l) => (
              <tr key={l}>
                <td className="p-2 font-bold text-[#0F6B3A]">{l}</td>
                {meses.map((m) => {
                  const cell = data.find((d) => d.mes === m && d.loja === l);
                  const ok = (cell?.acuracidade ?? 0) >= meta;
                  return (
                    <td key={m} className="p-1">
                      <div className={`rounded-md px-2 py-2 text-center text-xs font-bold ${cell ? (ok ? "bg-[#0F6B3A] text-white" : "bg-[#2DC26B] text-white") : "bg-[#F4F8F5] text-[#8A9A90]"}`}>
                        {cell ? `${(cell.acuracidade ?? 0).toFixed(1)}` : "—"}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-center gap-6 text-sm text-[#5A6B62]">
        <span className="flex items-center gap-2"><span className="w-4 h-4 rounded bg-[#0F6B3A]" /> Dentro da meta</span>
        <span className="flex items-center gap-2"><span className="w-4 h-4 rounded bg-[#2DC26B]" /> Abaixo da meta</span>
      </div>
    </div>,
    // 7 Plano de ação
    <div key="s7" className="w-full max-w-3xl space-y-6">
      <h2 className="text-3xl font-bold text-[#0F6B3A] text-center">Plano de Ação & Auditorias Cíclicas</h2>
      <div className="space-y-3">
        {acoes.length === 0 ? (
          <p className="text-center text-[#8A9A90] text-lg">Nenhuma ação registrada na Seção 3.</p>
        ) : acoes.map((a, i) => (
          <div key={a.id} className="flex items-start gap-4 bg-[#F4F8F5] border border-[#D9E3DC] rounded-xl p-4">
            <span className="w-8 h-8 rounded-full bg-[#2DC26B] text-white font-bold flex items-center justify-center shrink-0">{i + 1}</span>
            <div>
              {a.titulo && <p className="font-bold text-[#0F2A1F]">{a.titulo}</p>}
              <p className="text-[#5A6B62] whitespace-pre-wrap">{a.conteudo}</p>
            </div>
          </div>
        ))}
      </div>
    </div>,
    // 8 Conclusão
    <div key="s8" className="text-center space-y-6 max-w-2xl">
      <h2 className="text-3xl font-bold text-[#0F6B3A]">Conclusão & Certificação Operacional</h2>
      <div className="bg-[#EAF7EF] border border-[#D9E3DC] rounded-2xl p-8 space-y-4">
        <p className="text-lg text-[#0F2A1F]">
          Acuracidade da loja <strong>{loja}</strong> em <strong>{mes}</strong>:{" "}
          <strong className="text-[#0F6B3A] text-2xl">{(sel.acuracidade ?? 0).toFixed(2)}%</strong>
        </p>
        <p className="text-[#5A6B62]">
          {(sel.acuracidade ?? 0) >= meta
            ? "Operação certificada dentro da meta corporativa de acuracidade."
            : `Operação abaixo da meta de ${meta}%. Plano de ação corretiva em andamento.`}
        </p>
        <div className="pt-4 border-t border-[#D9E3DC] text-sm text-[#8A9A90]">
          ESTOQAR · Governança de Inventário · {new Date().toLocaleDateString("pt-BR")}
        </div>
      </div>
    </div>,
  ];

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="presentation-mode">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E3ECE6]">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-[#2DC26B] uppercase tracking-widest">Modo Apresentação</span>
          <span className="text-xs text-[#8A9A90]">Slide {slide + 1} de {TOTAL}</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="presentation-fullscreen-btn" onClick={toggleFullscreen} className="p-2 rounded-md text-[#5A6B62] hover:bg-[#F4F8F5]" title="Tela cheia">
            <Maximize className="w-5 h-5" />
          </button>
          <button data-testid="presentation-close-btn" onClick={onClose} className="p-2 rounded-md text-[#5A6B62] hover:bg-[#F4F8F5]" title="Fechar (Esc)">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto" data-testid={`presentation-slide-${slide + 1}`}>
        {slides[slide]}
      </div>

      <div className="flex items-center justify-between px-6 py-4 border-t border-[#E3ECE6]">
        <button data-testid="presentation-prev-btn" onClick={prev} disabled={slide === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D9E3DC] text-[#0F6B3A] font-medium disabled:opacity-40 hover:bg-[#F4F8F5]">
          <ChevronLeft className="w-4 h-4" /> Anterior
        </button>
        <div className="flex gap-2">
          {Array.from({ length: TOTAL }).map((_, i) => (
            <button key={i} data-testid={`presentation-dot-${i + 1}`} onClick={() => setSlide(i)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${i === slide ? "bg-[#2DC26B]" : "bg-[#D9E3DC] hover:bg-[#0F6B3A]/40"}`} />
          ))}
        </div>
        <button data-testid="presentation-next-btn" onClick={next} disabled={slide === TOTAL - 1}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#0F6B3A] to-[#0B5530] text-white font-medium disabled:opacity-40 border border-[#2DC26B]">
          Próximo <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
